import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import type { KronaFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { TaxaAggregationResult } from '../../Sinks/Selector/TaxaAggregation.ts'
import { aggregateTaxaAllBuckets } from '../../Sinks/Selector/TaxaAggregationCore.ts'

const selectorCache = createSelectorCache<TransformResult<TaxaAggregationResult>>()

export function selectTaxaAggregation(
  state: typeof State,
  sourceId: SourceId,
  _bucketKey: string | undefined,
  opts: KronaFilterOpts,
  optsKey: string
): TransformResult<TaxaAggregationResult> {
  const cacheId = `${sourceId}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold })
      ],
      (ctx, filtered): TransformResult<TaxaAggregationResult> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const value = aggregateTaxaAllBuckets({
          filteredBuckets: filtered,
          level: opts.selectedPath.length
        })

        return { status: 'ready', value }
      }
    )
  )(state)
}
